import { useRef, type KeyboardEvent } from 'react';

interface UseRovingRadioGroupParams<Value> {
    options: readonly Value[];
    selected: Value | null | undefined;
    onSelect: (value: Value) => void;
}

/**
 * The WAI-ARIA radio group pattern for options drawn as tiles: a single tab stop (the selected option,
 * else the first), arrow keys that move focus and selection together and wrap around, Space and Enter
 * that select the focused option. Spread `getRadioProps(value)` on the element that is the radio.
 */
export const useRovingRadioGroup = <Value extends string | number>({
    options,
    selected,
    onSelect,
}: UseRovingRadioGroupParams<Value>) => {
    const nodes = useRef(new Map<Value, HTMLElement>());
    const tabbable = options.find((option) => option === selected) ?? options[0];

    const selectAndFocus = (value: Value) => {
        onSelect(value);
        nodes.current.get(value)?.focus();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>, value: Value) => {
        const index = options.indexOf(value);

        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault();
                selectAndFocus(options[(index + 1) % options.length]);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault();
                selectAndFocus(options[(index - 1 + options.length) % options.length]);
                break;
            case ' ':
            case 'Enter':
                event.preventDefault();
                onSelect(value);
                break;
            default:
                break;
        }
    };

    const getRadioProps = (value: Value) => ({
        ref: (node: HTMLElement | null) => {
            if (node) {
                nodes.current.set(value, node);
            } else {
                nodes.current.delete(value);
            }
        },
        role: 'radio' as const,
        'aria-checked': value === selected,
        tabIndex: value === tabbable ? 0 : -1,
        onClick: () => onSelect(value),
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => handleKeyDown(event, value),
    });

    return { getRadioProps };
};

/** What an option of a roving radio group spreads on its radio element. */
export type RovingRadioProps = ReturnType<ReturnType<typeof useRovingRadioGroup>['getRadioProps']>;
